import { jsonBytes } from '../../../json';
import { ISSUE, ISSUE_TARGET, configIssue } from '../../catalog/issue';
import type { ConfigIssue } from '../../catalog/issue';
import { MAPPING_TIMEOUT, MAPPING_TRIGGER, LED_SELECTOR_LENGTH } from '../../catalog/mapping';
import { RCU_HTTP_LIMITS } from '../../catalog/protocol';
import { fail, ok } from '../../result';
import type { Result } from '../../result';
import type { RcuDocument } from '../../wire';

/**
 * Guards every outgoing document.
 *
 * The firmware does not reject bad values: it casts numbers to bytes and copies
 * JSON into a 512-byte buffer, truncating what does not fit. So a command is
 * checked here, once, and a builder that would produce something the RCU cannot
 * hold fails instead of sending it.
 */

/** A number field the firmware would truncate rather than refuse. */
export const fieldRange = (
  label: string,
  value: number,
  min: number,
  max: number,
): ConfigIssue | null =>
  Number.isInteger(value) && value >= min && value <= max
    ? null
    : configIssue(
        ISSUE.COMMAND_FIELD_OUT_OF_RANGE,
        { kind: ISSUE_TARGET.RCU },
        `${label} ${value} (allowed ${min}–${max})`,
      );

export const nonEmptyText = (label: string, value: string): ConfigIssue | null =>
  value.trim() === ''
    ? configIssue(ISSUE.COMMAND_FIELD_OUT_OF_RANGE, { kind: ISSUE_TARGET.RCU }, `${label} is empty`)
    : null;

/** A trigger or selector: whole numbers 0–255, of the length the schema needs. */
export const byteList = (
  label: string,
  values: readonly number[],
  expectedLength: number,
): ConfigIssue | null => {
  const lengthOk = values.length === expectedLength;
  const valuesOk = values.every(
    (value) => Number.isInteger(value) && value >= MAPPING_TRIGGER.MIN && value <= MAPPING_TRIGGER.MAX,
  );
  return lengthOk && valuesOk
    ? null
    : configIssue(
        ISSUE.COMMAND_FIELD_OUT_OF_RANGE,
        { kind: ISSUE_TARGET.RCU },
        `${label} [${values.join(',')}] (expected ${expectedLength} numbers ${MAPPING_TRIGGER.MIN}–${MAPPING_TRIGGER.MAX})`,
      );
};

/** `x=[group,panel,button,state]` of a scene. */
export const triggerIssue = (label: string, values: readonly number[]): ConfigIssue | null =>
  byteList(label, values, MAPPING_TRIGGER.LENGTH);

/** `x=[relayno,state]` or `x=[dintype,addr,channel,value]` of an LED mapping. */
export const ledSelectorIssue = (label: string, values: readonly number[]): ConfigIssue | null =>
  values.length === LED_SELECTOR_LENGTH.RELAY || values.length === LED_SELECTOR_LENGTH.DIN
    ? byteList(label, values, values.length)
    : configIssue(
        ISSUE.COMMAND_FIELD_OUT_OF_RANGE,
        { kind: ISSUE_TARGET.RCU },
        `${label} [${values.join(',')}] (expected ${LED_SELECTOR_LENGTH.RELAY} or ${LED_SELECTOR_LENGTH.DIN} numbers)`,
      );

/** Seconds a timed action waits before it is reversed or cancelled. */
export const timeoutIssue = (seconds: number): ConfigIssue | null =>
  fieldRange('Timeout', seconds, MAPPING_TIMEOUT.MIN, MAPPING_TIMEOUT.MAX);

/** The size rule, applied to every document: the RCU's buffer holds 511 bytes. */
const sizeIssue = (document: RcuDocument): ConfigIssue | null => {
  const bytes = jsonBytes(document);
  return bytes <= RCU_HTTP_LIMITS.MAX_COMMAND_BYTES
    ? null
    : configIssue(ISSUE.COMMAND_TOO_LARGE, { kind: ISSUE_TARGET.RCU }, `${bytes} bytes`);
};

/** The single exit every builder uses: first failed check wins, then the size rule. */
export const validated = (
  document: RcuDocument,
  checks: readonly (ConfigIssue | null)[] = [],
): Result<RcuDocument> => {
  const failed = checks.find((candidate): candidate is ConfigIssue => candidate !== null);
  if (failed !== undefined) return fail(failed);
  const oversize = sizeIssue(document);
  return oversize === null ? ok(document) : fail(oversize);
};
